import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoals } from '../lib/stores/goals';
import { useLifeAreas } from '../lib/stores/lifeareas';
import Card from '../lib/design-system/components/Card';
import Button from '../lib/design-system/components/Button';

export default function GoalDetailPage() {
  const { goalId } = useParams<{ goalId: string }>();
  const navigate = useNavigate();
  const {
    goals,
    milestones,
    loading,
    error,
    initialize,
    updateGoal,
    deleteGoal,
    loadMilestones,
    createMilestone,
    updateMilestone,
    deleteMilestone,
    toggleMilestoneComplete,
    initialized,
  } = useGoals();
  const { areas, initialize: initAreas, initialized: areasInitialized } = useLifeAreas();

  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showEditGoal, setShowEditGoal] = useState(false);
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    percentage: 10,
    dueDate: '',
  });
  const [goalForm, setGoalForm] = useState({
    title: '',
    description: '',
    targetDate: '',
    lifeAreaId: '',
  });

  useEffect(() => {
    if (!initialized) initialize();
    if (!areasInitialized) initAreas();
  }, [initialized, areasInitialized]);

  useEffect(() => {
    if (goalId && initialized) {
      loadMilestones(goalId);
    }
  }, [goalId, initialized]);

  const goal = goals.find(g => g.id === goalId);
  const goalMilestones = goalId ? (milestones[goalId] || []) : [];

  useEffect(() => {
    if (goal) {
      setGoalForm({
        title: goal.title,
        description: goal.description ?? '',
        targetDate: goal.target_date ?? '',
        lifeAreaId: goal.life_area_id ?? '',
      });
    }
  }, [goal]);

  if (loading && !initialized) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading goal...</p>
      </div>
    );
  }

  if (!goal) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-neutral-600)', marginBottom: '1rem' }}>
          Goal not found
        </p>
        <Button onClick={() => navigate('/purpose/goals')}>
          Back to Goals
        </Button>
      </div>
    );
  }

  const getAreaName = (areaId: string | null) => {
    if (!areaId) return 'No Area';
    const area = areas.find(a => a.id === areaId);
    return area ? area.name : 'Unknown Area';
  };

  const getAreaColor = (areaId: string | null) => {
    if (!areaId) return '#6B7280';
    const area = areas.find(a => a.id === areaId);
    return area ? area.color : '#6B7280';
  };

  const handleCreateMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (milestoneForm.title.trim() && goalId) {
      await createMilestone(goalId, {
        title: milestoneForm.title.trim(),
        percentage: milestoneForm.percentage,
        dueDate: milestoneForm.dueDate || undefined,
      });
      setMilestoneForm({ title: '', percentage: 10, dueDate: '' });
      setShowAddMilestone(false);
    }
  };

  const handleUpdateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (goalId) {
      await updateGoal(goalId, {
        title: goalForm.title.trim(),
        description: goalForm.description.trim() || undefined,
        targetDate: goalForm.targetDate || undefined,
        lifeAreaId: goalForm.lifeAreaId || undefined,
      });
      setShowEditGoal(false);
    }
  };

  const handleDeleteGoal = async () => {
    if (goalId && window.confirm('Are you sure you want to delete this goal?')) {
      await deleteGoal(goalId);
      navigate('/purpose/goals');
    }
  };

  const totalWeight = goalMilestones.reduce((sum, m) => sum + m.percentage, 0);
  const remainingWeight = 100 - totalWeight;

  return (
    <div style={{ padding: '2rem', width: '100%' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/purpose/goals')}
        style={{
          marginBottom: '1.5rem',
          padding: '0.5rem 1rem',
          border: '1px solid var(--color-neutral-300)',
          borderRadius: '6px',
          background: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.875rem',
        }}
      >
        ← Back to Goals
      </button>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1.5rem',
        }}>
          {error}
        </div>
      )}

      {/* Goal Header */}
      <Card style={{ padding: '2rem', marginBottom: '2rem' }}>
        {!showEditGoal ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <h1 style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: '2rem',
                  fontWeight: 500,
                  color: 'var(--color-neutral-900)',
                  marginBottom: '0.5rem',
                }}>
                  {goal.title}
                </h1>
                {goal.description && (
                  <p style={{
                    color: 'var(--color-neutral-600)',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    marginBottom: '1rem',
                  }}>
                    {goal.description}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {goal.life_area_id && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      backgroundColor: `${getAreaColor(goal.life_area_id)}15`,
                      color: getAreaColor(goal.life_area_id),
                      fontWeight: 500,
                    }}>
                      {getAreaName(goal.life_area_id)}
                    </span>
                  )}
                  {goal.target_date && (
                    <span style={{ fontSize: '0.875rem', color: 'var(--color-neutral-500)' }}>
                      Target: {new Date(goal.target_date).toLocaleDateString()}
                    </span>
                  )}
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    backgroundColor: goal.status === 'completed' ? 'var(--color-success-100)' : 'var(--color-primary-100)',
                    color: goal.status === 'completed' ? 'var(--color-success-700)' : 'var(--color-primary-700)',
                  }}>
                    {goal.status === 'completed' ? 'Completed' : 'Active'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowEditGoal(true)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={handleDeleteGoal}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid var(--color-error-300)',
                    borderRadius: '6px',
                    background: 'white',
                    color: 'var(--color-error-600)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-neutral-700)' }}>
                  Overall Progress
                </span>
                <span style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: goal.status === 'completed' ? 'var(--color-success-600)' : 'var(--color-primary-600)',
                }}>
                  {goal.progress}%
                </span>
              </div>
              <div style={{
                height: '12px',
                backgroundColor: 'var(--color-neutral-200)',
                borderRadius: '6px',
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${goal.progress}%`,
                  backgroundColor: goal.status === 'completed' ? 'var(--color-success-600)' : 'var(--color-primary-600)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          </>
        ) : (
          <form onSubmit={handleUpdateGoal}>
            <h3 style={{ marginBottom: '1rem', fontSize: '1.25rem' }}>Edit Goal</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
              }}>
                Title *
              </label>
              <input
                type="text"
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
              }}>
                Description
              </label>
              <textarea
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                }}>
                  Life Area
                </label>
                <select
                  value={goalForm.lifeAreaId}
                  onChange={(e) => setGoalForm({ ...goalForm, lifeAreaId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                >
                  <option value="">No Area</option>
                  {areas.map(area => (
                    <option key={area.id} value={area.id}>
                      {area.icon} {area.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                }}>
                  Target Date
                </label>
                <input
                  type="date"
                  value={goalForm.targetDate}
                  onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button type="submit">Save Changes</Button>
              <button
                type="button"
                onClick={() => setShowEditGoal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Card>

      {/* Milestones Section */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '1.75rem',
          fontWeight: 500,
          color: 'var(--color-neutral-900)',
        }}>
          Milestones ({goalMilestones.length})
        </h2>
        <Button onClick={() => setShowAddMilestone(!showAddMilestone)}>
          {showAddMilestone ? 'Cancel' : '+ Add Milestone'}
        </Button>
      </div>

      {/* Weight Warning */}
      {remainingWeight < 0 && (
        <div style={{
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#DC2626',
          marginBottom: '1rem',
          fontSize: '0.875rem',
        }}>
          ⚠️ Total milestone weight exceeds 100% ({totalWeight}%). Adjust milestone percentages.
        </div>
      )}

      {/* Add Milestone Form */}
      {showAddMilestone && (
        <Card style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
          <form onSubmit={handleCreateMilestone}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Add Milestone</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '0.5rem',
                color: 'var(--color-neutral-700)',
              }}>
                Title *
              </label>
              <input
                type="text"
                value={milestoneForm.title}
                onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                required
                placeholder="e.g., Complete initial research"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '6px',
                  fontSize: '1rem',
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                }}>
                  Weight (% of goal) *
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={milestoneForm.percentage}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, percentage: parseInt(e.target.value) })}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--color-neutral-500)', marginTop: '0.25rem' }}>
                  Remaining: {remainingWeight}%
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--color-neutral-700)',
                }}>
                  Due Date
                </label>
                <input
                  type="date"
                  value={milestoneForm.dueDate}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, dueDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-neutral-300)',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button type="submit" disabled={!milestoneForm.title.trim()}>
                Add Milestone
              </Button>
              <button
                type="button"
                onClick={() => setShowAddMilestone(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid var(--color-neutral-300)',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Milestones List */}
      {goalMilestones.length === 0 ? (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-neutral-500)' }}>
            No milestones yet. Add milestones to track progress toward this goal.
          </p>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {goalMilestones.map(milestone => (
            <Card key={milestone.id} style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
                <button
                  onClick={() => goalId && toggleMilestoneComplete(goalId, milestone.id)}
                  style={{
                    minWidth: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: milestone.completed_at ? 'none' : '2px solid var(--color-neutral-400)',
                    background: milestone.completed_at ? 'var(--color-success-600)' : 'transparent',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                  }}
                >
                  {milestone.completed_at && '✓'}
                </button>

                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: milestone.completed_at ? 'var(--color-neutral-500)' : 'var(--color-neutral-900)',
                      textDecoration: milestone.completed_at ? 'line-through' : 'none',
                      margin: 0,
                    }}>
                      {milestone.title}
                    </h4>
                    <button
                      onClick={() => goalId && deleteMilestone(goalId, milestone.id)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--color-error-600)',
                        cursor: 'pointer',
                        fontSize: '1rem',
                      }}
                      title="Delete milestone"
                    >
                      ×
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem', color: 'var(--color-neutral-600)' }}>
                    <span style={{ fontWeight: 600 }}>
                      {milestone.percentage}% of goal
                    </span>
                    {milestone.due_date && (
                      <span>
                        Due: {new Date(milestone.due_date).toLocaleDateString()}
                      </span>
                    )}
                    {milestone.completed_at && (
                      <span style={{ color: 'var(--color-success-600)' }}>
                        ✓ Completed {new Date(milestone.completed_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
